/**
 * Copyright Warner Bros. Entertainment, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Warner Bros. Entertainment, Inc. and its suppliers, if any.
 * The intellectual and technical concepts contained herein are
 * proprietary to Warner Bros. Entertainment, Inc. and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in process,
 * and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * unlawful and strictly forbidden unless prior written permission is
 * obtained from Warner Bros. Entertainment, Inc.
 */

const MINIMUM_SEARCH_LENGTH = 3;

/**
 * Whether the input value has at least one word long enough to search on
 * @param  {string} inputValue
 * @return {boolean}
 */
const isSearchable = (inputValue) => inputValue
    .trim()
    .split(/\s+/)
    .some(word => word.length >= MINIMUM_SEARCH_LENGTH);

/**
 * Returns the menu hint for the current search state, or null when none applies
 */
const getHint = ({
    errorHint,
    hasError = false,
    hasMatches = false,
    inputValue = '',
    isLoading = false,
    noMatchesHint,
    searchTerm = '',
    tooShortHint
}) => {
    const input = inputValue.trim();

    if (!isSearchable(input)) {
        return tooShortHint;
    }

    if (isLoading || searchTerm !== input) {
        return null;
    }

    if (hasError) {
        return errorHint;
    }

    if (!hasMatches) {
        return noMatchesHint;
    }

    return null;
};

const normalizeText = value => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/**
 * Whether every input word is included in the contact's full name
 * @param  {Object} contact
 * @param  {string} contact.firstName
 * @param  {string} [contact.middleName]
 * @param  {string} contact.lastName
 * @param  {string} input
 * @return {boolean}
 */

const includesInputWords = (contact, input) => {
    const normalizedInput = normalizeText(input.trim());

    if (!normalizedInput) {
        return false;
    }

    const fullName = normalizeText([
        contact.firstName,
        contact.middleName,
        contact.lastName
    ].join(' '));

    return normalizedInput
        .split(/\s+/)
        .every(word => fullName.includes(word));
};

export {isSearchable, getHint, includesInputWords};
